import React, { useState } from 'react';
import { Card, Form, Input, Button, Typography, Space, Divider, Row, Col, message, Modal, Tooltip } from 'antd';
import { UserOutlined, MailOutlined, LockOutlined, CloudOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { customFetch } from '../utils/api';

const { Title } = Typography;

export const SettingsPage: React.FC = () => {
  const { user, logout, setUser } = useAuth();
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [isEmailModalVisible, setIsEmailModalVisible] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const onProfileUpdate = async (values: any) => {
    setProfileLoading(true);
    try {
      const response = await customFetch('/api/profile/', {
        method: 'PATCH', // PATCH is used to partially update the resource
        body: JSON.stringify(values),
      });

      if (response.ok) {
        const updatedUser = await response.json();
        setUser(updatedUser); // Update the user in the AuthContext
        message.success('Profile updated successfully!');
      } else {
        const errorData = await response.json();
        const errorFields = Object.keys(errorData).map(key => ({
            name: key,
            errors: Array.isArray(errorData[key]) ? errorData[key] : [errorData[key]],
        }));
        profileForm.setFields(errorFields);
        message.error('Failed to update profile. Please check the errors below.');
      }
    } catch (error) {
      message.error('An unexpected error occurred.');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleEmailChangeRequest = () => {
    setIsEmailModalVisible(true);
  };

  const handleEmailChangeConfirm = async () => {
    if (!newEmail) {
      message.error("Please enter a new email address.");
      return;
    }
    try {
      const response = await customFetch('/api/change-email/', {
        method: 'POST',
        body: JSON.stringify({ email: newEmail }),
      });
      if (response.ok) {
        message.success("A confirmation email has been sent to your new address. Please check your inbox.");
        setIsEmailModalVisible(false);
        setNewEmail('');
      } else {
        const errorData = await response.json();
        message.error(errorData.email[0] || "Failed to request email change.");
      }
    } catch (error) {
      message.error("An unexpected error occurred.");
    }
  };


  const onPasswordChange = async (values: any) => {
    setLoading(true);
    try {
      const response = await customFetch('/api/change-password/', {
        method: 'POST',
        body: JSON.stringify({
          old_password: values.current_password,
          new_password: values.new_password,
          confirm_password: values.confirm_password,
        }),
      });

      if (response.ok) {
        message.success('Password changed successfully! Please log in again.');
        passwordForm.resetFields();
        // For security, log the user out after a password change
        await logout();
        // Navigate to login handled by AuthContext or App router
      } else {
        const errorData = await response.json();
        // Antd's Form can display errors directly on fields
        const errorFields = Object.keys(errorData).map(key => ({
            name: key === 'new_password' ? ['new_password', 'confirm_password'] : key,
            errors: Array.isArray(errorData[key]) ? errorData[key] : [errorData[key]],
        }));
        passwordForm.setFields(errorFields);
        message.error('Failed to change password. Please check the errors below.');
      }
    } catch (error) {
      message.error('An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Title level={2}>Account Settings</Title>
      <p>Manage your account preferences and security settings.</p>

      <Row gutter={[24, 24]}>
        {/* Profile Settings */}
        <Col xs={24} lg={12}>
          <Card title="Profile Information" style={{ height: '100%' }}>
            <Form
              form={profileForm}
              layout="vertical"
              initialValues={{
                email: user?.email,
                username: user?.username,
                first_name: user?.first_name,
                last_name: user?.last_name,
              }}
              onFinish={onProfileUpdate}
            >
              <Form.Item
                name="email"
                label="Email"
              >
                <Input
                  prefix={
                    <Tooltip title="Your email address is your primary login and cannot be changed here.">
                      <MailOutlined />
                    </Tooltip>
                  }
                  readOnly
                />
              </Form.Item>
              {/* <Button onClick={handleEmailChangeRequest} style={{marginBottom: '24px'}}>
                Change Email
              </Button> */}

              <Form.Item
                name="username"
                label="Username"
              >
                <Input
                  prefix={
                    <Tooltip title="Your username is a permanent identifier and cannot be changed.">
                      <UserOutlined />
                    </Tooltip>
                  }
                  readOnly
                />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="first_name"
                    label="First Name"
                    rules={[{ required: true, message: 'Please input your first name!' }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="last_name"
                    label="Last Name"
                    rules={[{ required: true, message: 'Please input your last name!' }]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={profileLoading}>
                  Update Profile
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* Password Change */}
        <Col xs={24} lg={12}>
          <Card title="Change Password" style={{ height: '100%' }}>
            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={onPasswordChange}
            >
              <Form.Item
                name="current_password"
                label="Current Password"
                rules={[{ required: true, message: 'Please input your current password!' }]}
              >
                <Input.Password prefix={<LockOutlined />} />
              </Form.Item>

              <Form.Item
                name="new_password"
                label="New Password"
                rules={[
                  { required: true, message: 'Please input your new password!' },
                  { min: 8, message: 'Password must be at least 8 characters!' }
                ]}
              >
                <Input.Password prefix={<LockOutlined />} />
              </Form.Item>

              <Form.Item
                name="confirm_password"
                label="Confirm New Password"
                dependencies={['new_password']}
                rules={[
                  { required: true, message: 'Please confirm your new password!' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('new_password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('The two passwords do not match!'));
                    },
                  }),
                ]}
              >
                <Input.Password prefix={<LockOutlined />} />
              </Form.Item>

              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Change Password
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* S3 Configuration */}
        {user?.is_staff && (
          <Col xs={24}>
            <Card title="S3 Storage Configuration" extra={<CloudOutlined />}>
              <p style={{ color: '#666', marginBottom: '24px' }}>
                Configure your S3 storage settings for file uploads. These settings are managed by administrators.
              </p>
              
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <strong>Storage Quota:</strong> {user ? '1 GB' : 'Not available'}
                </div>
                <div>
                  <strong>Used Space:</strong> {user ? '0 MB' : 'Not available'}
                </div>
                <div>
                  <strong>Available Space:</strong> {user ? '1 GB' : 'Not available'}
                </div>
              </Space>

              <Divider />

              <div style={{ color: '#999' }}>
                <p>For S3 configuration changes, please contact your administrator.</p>
              </div>
            </Card>
          </Col>
        )}
      </Row>
      {/* <Modal
        title="Change Email Address"
        visible={isEmailModalVisible}
        onOk={handleEmailChangeConfirm}
        onCancel={() => setIsEmailModalVisible(false)}
        okText="Send Confirmation"
      >
        <p>Please enter the new email address. A confirmation link will be sent there to verify ownership.</p>
        <Input 
          placeholder="New email address"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          type="email"
        />
      </Modal> */}
    </div>
  );
}; 